import { RenderProgress } from "@/components/mist/RenderProgress";
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Sparkles, Upload, Film,
  Type, Image as ImageIcon, Star, Wand2, Play, Check, ScanLine, Pencil, X,
} from "lucide-react";
import { pipelinesApi, streamAsset } from "@/api/mist";
import VaultPickButton from "@/components/mist/VaultPickButton";

const ASPECTS = [{ k: "9:16", r: 9 / 16 }, { k: "1:1", r: 1 }, { k: "16:9", r: 16 / 9 }];
const FONTS = [
  { k: "serif", label: "Serif", css: "Georgia, serif" },
  { k: "sans", label: "Sans", css: "Arial, sans-serif" },
  { k: "mono", label: "Mono", css: "monospace" },
  { k: "display", label: "Display", css: "Impact, Arial, sans-serif" },
];
const ZONES = ["top-left", "top-center", "top-right", "mid-left", "center", "mid-right", "bottom-left", "bottom-center", "bottom-right"];
const ZONE_POS = {
  "top-left": [28, 16], "top-center": [50, 16], "top-right": [72, 16],
  "mid-left": [28, 50], "center": [50, 50], "mid-right": [72, 50],
  "bottom-left": [28, 84], "bottom-center": [50, 84], "bottom-right": [72, 84],
};
const ANIMS = ["fade", "slide_up", "slide_down", "slide_left", "slide_right", "pop", "typewriter"];
const MOTIONS = [
  { k: "none", label: "None" },
  { k: "swing", label: "Swing L/R" },
  { k: "sway", label: "Sway L/R" },
  { k: "zoom_in", label: "Zoom In" },
  { k: "zoom_out", label: "Zoom Out" },
  { k: "float", label: "Float" },
  { k: "pulse", label: "Pulse" },
];
const TEXT_MOTIONS = [
  { k: "none", label: "None" },
  { k: "sway", label: "Sway L/R" },
  { k: "float", label: "Float" },
  { k: "pulse", label: "Pulse" },
];
const PERIODIC = ["swing", "sway", "float", "pulse"];
const speedLabel = (v) => { const s = v ?? 0.5; return s < 0.4 ? "Gentle" : s < 0.7 ? "Medium" : s < 1.0 ? "Lively" : "Punchy"; };
const motionDur = (l) => (PERIODIC.includes(l.motion) ? { animationDuration: `${(1 / (l.swing_speed || 0.5)).toFixed(2)}s` } : {});

const newTextLayer = () => ({
  type: "text", text: "Your text", font: "sans", size: 64, color: "#FFFFFF",
  zone: "center", align: "center", animation: "fade", motion: "none", swing_speed: 0.5, start: 0.2, end: 5, anim_dur: 0.6, tracking: 0,
});
const newLogoLayer = () => ({
  type: "image", scale: 0.4, zone: "center", animation: "pop", motion: "swing", swing_speed: 0.5, start: 0.2, end: 5, anim_dur: 0.6,
});

const TEMPLATES = {
  Blank: () => ({ aspect: "9:16", duration: 5, background: { type: "gradient", color: "#0d0d14", color2: "#000000" }, layers: [newTextLayer()] }),
  "Neon Title": () => ({
    aspect: "9:16", duration: 5, background: { type: "gradient", color: "#0d0d14", color2: "#000000" },
    layers: [
      { ...newTextLayer(), text: "MY BRAND", font: "serif", size: 118, color: "#00F5D4", zone: "center", animation: "pop", start: 0.2, end: 5 },
      { ...newTextLayer(), text: "JOIN MYSITE.COM", font: "sans", size: 44, color: "#FFD028", zone: "bottom-center", animation: "slide_up", start: 1.2, end: 5 },
    ],
  }),
  "Handle Card": () => ({
    aspect: "9:16", duration: 6, background: { type: "color", color: "#111111" },
    layers: [
      { ...newTextLayer(), text: "FOLLOW", font: "display", size: 96, color: "#FFFFFF", zone: "center", animation: "slide_up", start: 0.3, end: 6 },
      { ...newTextLayer(), text: "@yourhandle", font: "mono", size: 50, color: "#38BDF8", zone: "bottom-center", animation: "typewriter", start: 1.4, end: 6 },
    ],
  }),
  "Cinematic Minimalist": () => ({
    aspect: "9:16", duration: 5, variant: "cinematic_minimalist",
    background: { type: "gradient", color: "#1b1b24", color2: "#000000" },
    layers: [
      { ...newTextLayer(), text: "MY BRAND", font: "serif", size: 104, color: "#FDF6E3", zone: "center", animation: "fade", start: 0.4, end: 5 },
      { ...newTextLayer(), text: "cinematic stories", font: "sans", size: 40, color: "#FFD028", zone: "center", y: 0.6, animation: "fade", start: 1.0, end: 5 },
    ],
  }),
  "Creator Card": () => ({
    aspect: "9:16", duration: 6, variant: "creator_card",
    background: { type: "color", color: "#0c0c0e" },
    layers: [
      { ...newTextLayer(), text: "YOUR NAME", font: "display", size: 72, color: "#FFFFFF", zone: "center", y: 0.52, animation: "slide_up", start: 0.4, end: 6 },
      { ...newTextLayer(), text: "@yourhandle", font: "mono", size: 42, color: "#FFD028", zone: "center", y: 0.6, animation: "fade", start: 1.0, end: 6 },
      { ...newTextLayer(), text: "FOLLOW FOR MORE", font: "sans", size: 30, color: "#FDF6E3", zone: "center", y: 0.66, animation: "fade", start: 1.6, end: 6 },
    ],
  }),
  "Tech Interface Grid": () => ({
    aspect: "9:16", duration: 5, variant: "tech_grid",
    background: { type: "color", color: "#04070a" },
    layers: [
      { ...newTextLayer(), text: "SYSTEM", font: "mono", size: 96, color: "#00F5D4", zone: "center", y: 0.44, animation: "typewriter", start: 0.3, end: 5 },
      { ...newTextLayer(), text: "@yourhandle", font: "mono", size: 40, color: "#FFFFFF", zone: "center", y: 0.56, animation: "fade", start: 1.2, end: 5 },
    ],
  }),
  "Clean Slate Lower Third": () => ({
    aspect: "9:16", duration: 5, variant: "lower_third",
    background: { type: "gradient", color: "#14141c", color2: "#000000" },
    layers: [
      { ...newTextLayer(), text: "YOUR NAME", font: "display", size: 68, color: "#FFFFFF", zone: "center", y: 0.68, animation: "fade", start: 0.4, end: 5 },
      { ...newTextLayer(), text: "@yourhandle · follow", font: "mono", size: 36, color: "#FFD028", zone: "center", y: 0.73, animation: "fade", start: 1.0, end: 5 },
    ],
  }),
};

/* ---- video tile that streams a saved outro asset ---- */
function OutroTile({ o, onDefault, onRename, onDelete, selectMode, selected, onToggleSelect }) {
  const [src, setSrc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(o.title || "");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    let url; streamAsset(o.id).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [o.id]);
  const commit = () => { const v = draft.trim(); setEditing(false); if (v && v !== o.title) onRename(o, v); };
  return (
    <div className={`border-2 rounded-xl overflow-hidden bg-black ${selectMode && selected ? "border-gold ring-2 ring-gold" : "border-[#2a2a2a]"}`} data-testid={`outro-tile-${o.id}`}>
      <div className="relative">
        {src ? <video src={src} className="w-full aspect-[9/16] object-cover bg-black" muted loop playsInline
          onMouseEnter={(e) => { if (!selectMode) { const p = e.target.play(); if (p) p.catch(() => {}); } }} onMouseLeave={(e) => e.target.pause()} controls={!selectMode} />
          : <div className="w-full aspect-[9/16] grid place-items-center"><Loader2 className="animate-spin text-[#00F5D4]" /></div>}
        {selectMode && (
          <button onClick={() => onToggleSelect(o.id)} data-testid={`outro-select-${o.id}`}
            className="absolute inset-0 grid place-items-start justify-end p-2 bg-black/20">
            <span className={`w-7 h-7 grid place-items-center rounded-full border-2 ${selected ? "bg-gold border-black text-black" : "bg-black/70 border-white/70 text-transparent"}`}><Check size={16} strokeWidth={3} /></span>
          </button>
        )}
      </div>
      {!selectMode && (
        <div className="p-2 space-y-1.5">
          {editing ? (
            <div className="flex items-center gap-1">
              <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && commit()}
                data-testid={`outro-rename-input-${o.id}`} className="flex-1 min-w-0 bg-[#141414] border border-gold text-white font-mono text-[10px] px-1.5 py-1 outline-none" />
              <button onClick={commit} data-testid={`outro-rename-save-${o.id}`} className="w-6 h-6 grid place-items-center bg-gold text-black rounded"><Check size={12} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="flex-1 font-mono text-[10px] text-white truncate">{o.title}</span>
              <button onClick={() => { setDraft(o.title || ""); setEditing(true); }} data-testid={`outro-rename-${o.id}`} className="w-6 h-6 grid place-items-center text-gold active:scale-90"><Pencil size={11} /></button>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <button onClick={() => onDefault(o)} data-testid={`outro-default-${o.id}`}
              className={`flex-1 flex items-center justify-center gap-1 font-mono text-[8px] font-black uppercase px-2 py-1 rounded-full border ${o.is_default ? "bg-gold text-black border-black" : "border-white/30 text-white/60"}`}>
              <Star size={10} fill={o.is_default ? "currentColor" : "none"} /> {o.is_default ? "Default" : "Set"}
            </button>
            {confirm ? (
              <>
                <button onClick={() => { setConfirm(false); onDelete(o); }} data-testid={`outro-delete-confirm-${o.id}`} className="flex items-center justify-center gap-1 font-mono text-[8px] font-black uppercase px-2 py-1 rounded-full border border-red-500 bg-red-500 text-white">Delete</button>
                <button onClick={() => setConfirm(false)} data-testid={`outro-delete-cancel-${o.id}`} className="w-6 h-6 grid place-items-center text-white/60 border border-white/25 rounded-full"><X size={11} /></button>
              </>
            ) : (
              <button onClick={() => setConfirm(true)} data-testid={`outro-delete-${o.id}`} className="w-7 h-7 grid place-items-center text-red-400 border border-white/20 rounded-full active:scale-90"><Trash2 size={12} /></button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const FieldLabel = ({ children }) => (
  <span className="block mb-1 font-mono text-[9px] font-black uppercase tracking-[0.14em] text-white/55">{children}</span>
);

export default function OutroVault({ onBack, templateId }) {
  const [view, setView] = useState("list");   // "list" | "editor"
  const [outros, setOutros] = useState([]);
  const [loading, setLoading] = useState(true);

  const [spec, setSpec] = useState(TEMPLATES.Blank());
  const [name, setName] = useState("My Outro");
  const [bgAssetId, setBgAssetId] = useState("");
  const [bgUrl, setBgUrl] = useState(null);
  const [logoAssetId, setLogoAssetId] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [musicPrompt, setMusicPrompt] = useState("");
  const [scenes, setScenes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [isolate, setIsolate] = useState(false);       // "Isolate Subject & Freeze Face" toggle
  const [isolating, setIsolating] = useState(false);   // laser-scan extraction in flight
  const bgRef = useRef(null);
  const logoRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const d = await pipelinesApi.outroList(); setOutros(d.outros || []); }
    catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { pipelinesApi.scenesList().then((d) => setScenes(d.scenes || [])).catch(() => {}); }, []);

  // Hide the founder PiP test-feed on mobile while this editor is open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);

  // Deep-link editing: load a saved outro's spec straight into the editor.
  useEffect(() => {
    if (!templateId) return;
    let alive = true;
    pipelinesApi.outroSpec(templateId).then((d) => {
      if (!alive || !d?.spec) return;
      setSpec(d.spec);
      setName(d.title || "My Outro");
      // Restore the saved background image + logo so the canvas renders intact.
      setBgAssetId(d.background_asset_id || "");
      setLogoAssetId(Object.values(d.layer_asset_ids || {})[0] || "");
      setMusicPrompt("");
      setIsolate(false);
      setView("editor");
      toast.success(`Editing "${d.title || "outro"}"`);
    }).catch(() => toast.error("Could not load that outro for editing"));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);
  useEffect(() => {
    let url;
    if (bgAssetId) streamAsset(bgAssetId).then((u) => { url = u; setBgUrl(u); }).catch(() => {});
    else setBgUrl(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [bgAssetId]);
  useEffect(() => {
    let url;
    if (logoAssetId) streamAsset(logoAssetId).then((u) => { url = u; setLogoUrl(u); }).catch(() => {});
    else setLogoUrl(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [logoAssetId]);

  const patch = (p) => setSpec((s) => ({ ...s, ...p }));
  const patchBg = (p) => setSpec((s) => ({ ...s, background: { ...s.background, ...p } }));
  const patchLayer = (i, p) => setSpec((s) => ({ ...s, layers: s.layers.map((l, idx) => idx === i ? { ...l, ...p } : l) }));
  const addLayer = (fn) => setSpec((s) => ({ ...s, layers: [...s.layers, fn()] }));
  const removeLayer = (i) => setSpec((s) => ({ ...s, layers: s.layers.filter((_, idx) => idx !== i) }));

  const startNew = (tplKey) => {
    setSpec(TEMPLATES[tplKey]()); setName(tplKey === "Blank" ? "My Outro" : tplKey);
    setBgAssetId(""); setLogoAssetId(""); setMusicPrompt(""); setView("editor");
  };

  const uploadBg = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return; setBusy(true);
    try { const r = await pipelinesApi.uploadSeed(f); setBgAssetId(r.asset_id); setIsolate(false); patchBg({ type: "image" }); toast.success("Background set"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  const fixBg = async () => {
    if (!bgAssetId) return; setBusy(true);
    try { const r = await pipelinesApi.enhanceCover(bgAssetId); setBgAssetId(r.asset_id); toast.success("Image AI-fixed"); }
    catch { toast.error("AI fix unavailable (needs fal key)"); } finally { setBusy(false); }
  };
  const toggleIsolate = async () => {
    if (isolate) { setIsolate(false); return; }        // turning it off just clears the lock
    if (!bgAssetId) { toast.error("Set a background image first"); return; }
    setIsolate(true); setIsolating(true);
    try {
      const { job_id } = await pipelinesApi.isolateSubject(bgAssetId);
      const j = await pipelinesApi.poll(job_id, { timeout: 180000 });
      if (j.status === "completed" && j.asset_id) {
        setBgAssetId(j.asset_id);
        toast.success("Subject isolated ✓ identity frozen");
      } else { toast.error(j.error || "Isolation failed"); setIsolate(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Isolation unavailable (needs fal key)");
      setIsolate(false);
    } finally { setIsolating(false); }
  };
  const uploadLogo = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return; setBusy(true);
    try {
      const r = await pipelinesApi.uploadSeed(f); setLogoAssetId(r.asset_id);
      if (!spec.layers.some((l) => l.type === "image")) addLayer(newLogoLayer);
      toast.success("Logo added");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const render = async () => {
    if (!spec.layers.length) { toast.error("Add at least one layer"); return; }
    const layer_asset_ids = {};
    spec.layers.forEach((l, i) => { if (l.type === "image" && logoAssetId) layer_asset_ids[i] = logoAssetId; });
    setBusy(true);
    try {
      const { job_id } = await pipelinesApi.outroRender({
        spec, name, background_asset_id: spec.background.type === "image" ? bgAssetId : null,
        layer_asset_ids, music_prompt: musicPrompt || null,
      });
      const j = await pipelinesApi.poll(job_id, { timeout: 300000 });
      if (j.status === "completed" && j.asset_id) {
        toast.success("Outro saved to your vault ✓");
        await refresh(); setView("list");
      } else { toast.error(j.error || "Render failed"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    finally { setBusy(false); }
  };

  const setDefault = async (o) => {
    try { await pipelinesApi.outroSetDefault(o.id); setOutros((p) => p.map((x) => ({ ...x, is_default: x.id === o.id }))); toast.success(`“${o.title}” is now your default outro`); }
    catch { toast.error("Could not set default"); }
  };

  const renameOutro = async (o, newName) => {
    try { await pipelinesApi.outroRename(o.id, newName); setOutros((p) => p.map((x) => x.id === o.id ? { ...x, title: newName } : x)); toast.success("Outro renamed"); }
    catch { toast.error("Rename failed"); }
  };
  const deleteOutro = async (o) => {
    try { await pipelinesApi.outroDelete(o.id); setOutros((p) => p.filter((x) => x.id !== o.id)); toast.success("Outro deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSel = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelect = () => { setSelectMode(false); setSelectedIds(new Set()); };
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => pipelinesApi.outroDelete(id)));
      setOutros((p) => p.filter((x) => !selectedIds.has(x.id)));
      toast.success(`${ids.length} outro${ids.length > 1 ? "s" : ""} deleted`);
      exitSelect();
    } catch { toast.error("Bulk delete failed"); }
  };

  const asp = ASPECTS.find((a) => a.k === spec.aspect) || ASPECTS[0];
  const bg = spec.background.type === "image" && bgUrl
    ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
    : spec.background.type === "gradient"
      ? { background: `linear-gradient(${spec.background.color}, ${spec.background.color2 || "#000"})` }
      : { background: spec.background.color };

  return (
    <div className="h-full w-full flex flex-col" data-testid="outro-vault">
      <style>{`
        @keyframes mistScan { 0% { top: -4%; } 100% { top: 104%; } }
        @keyframes mistSwing { 0%,100% { transform: rotate(-11deg); } 50% { transform: rotate(11deg); } }
        @keyframes mistSway { 0%,100% { transform: translateX(-5%); } 50% { transform: translateX(5%); } }
        @keyframes mistZoomIn { 0% { transform: scale(1); } 100% { transform: scale(1.2); } }
        @keyframes mistZoomOut { 0% { transform: scale(1.2); } 100% { transform: scale(1); } }
        @keyframes mistFloat { 0%,100% { transform: translateY(-6%); } 50% { transform: translateY(6%); } }
        @keyframes mistPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.07); } }
        .mist-motion-swing { animation: mistSwing 2s ease-in-out infinite; }
        .mist-motion-sway { animation: mistSway 2s ease-in-out infinite; }
        .mist-motion-zoom_in { animation: mistZoomIn 4s ease-in-out infinite alternate; }
        .mist-motion-zoom_out { animation: mistZoomOut 4s ease-in-out infinite alternate; }
        .mist-motion-float { animation: mistFloat 2.6s ease-in-out infinite; }
        .mist-motion-pulse { animation: mistPulse 1.8s ease-in-out infinite; }
        .mist-scan-beam { position: absolute; left: 0; right: 0; height: 3px; background: #39FF14;
          box-shadow: 0 0 10px 3px rgba(57,255,20,0.85), 0 0 24px 7px rgba(57,255,20,0.4);
          animation: mistScan 1.5s linear infinite; z-index: 5; }
        .mist-checker { background-color: #FDFBF7;
          background-image: linear-gradient(45deg, #1A1A1A 25%, transparent 25%, transparent 75%, #1A1A1A 75%, #1A1A1A),
                            linear-gradient(45deg, #1A1A1A 25%, transparent 25%, transparent 75%, #1A1A1A 75%, #1A1A1A);
          background-size: 20px 20px; background-position: 0 0, 10px 10px; }
      `}</style>
      <div className="shrink-0 px-4 pt-3 pb-1 flex items-center justify-between">
        <button onClick={view === "editor" ? () => setView("list") : onBack} data-testid="outro-back"
          className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
          <ArrowLeft size={13} /> {view === "editor" ? "Outro Vault" : "Mist Library"}
        </button>
        <span className="font-mono text-[9px] font-black uppercase tracking-widest text-[#00F5D4]">Outro Vault</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-28 pt-2">
        {view === "list" ? (
          <div className="space-y-4" data-testid="outro-list">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-white">Your outros</h3>
              {outros.length > 0 && (
                selectMode ? (
                  <button onClick={exitSelect} data-testid="outro-select-cancel" className="font-mono text-[9px] font-black uppercase tracking-widest text-white/60 border-2 border-white/25 rounded-lg px-2.5 py-1.5">Cancel</button>
                ) : (
                  <button onClick={() => setSelectMode(true)} data-testid="outro-select-mode" className="font-mono text-[9px] font-black uppercase tracking-widest text-gold border-2 border-black bg-charcoal rounded-lg px-2.5 py-1.5 shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">Select</button>
                )
              )}
            </div>
            {!selectMode && (
            <div>
              <FieldLabel>Create new</FieldLabel>
              <div className="grid grid-cols-2 gap-2" data-testid="outro-templates">
                {Object.keys(TEMPLATES).map((k) => (
                  <button key={k} onClick={() => startNew(k)} data-testid={`outro-tpl-${k.replace(/\s+/g, "-")}`}
                    className="flex items-center gap-2 border-2 border-black bg-charcoal text-white font-mono text-[10px] font-black uppercase tracking-wide py-3 px-3 rounded-lg shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                    {k === "Blank" ? <Plus size={14} className="text-[#00F5D4]" /> : <Sparkles size={14} className="text-gold" />} {k}
                  </button>
                ))}
              </div>
            </div>
            )}
            {loading ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[#00F5D4]" /></div>
              : outros.length ? (
                <div className="grid grid-cols-2 gap-3" data-testid="outro-grid">
                  {outros.map((o) => <OutroTile key={o.id} o={o} onDefault={setDefault} onRename={renameOutro} onDelete={deleteOutro}
                    selectMode={selectMode} selected={selectedIds.has(o.id)} onToggleSelect={toggleSel} />)}
                </div>
              ) : (
                <p className="font-mono text-[10px] text-white/50 border-2 border-dashed border-[#2a2a2a] rounded-xl p-4 text-center">
                  No outros yet. Pick a template above to build your first one.
                </p>
              )}
            {selectMode && (
              <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-black/95 border-t-2 border-[#2a2a2a] flex items-center gap-3" data-testid="outro-bulk-bar">
                <span className="flex-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/70" data-testid="outro-bulk-count">{selectedIds.size} selected</span>
                <button onClick={bulkDelete} disabled={selectedIds.size === 0} data-testid="outro-bulk-delete"
                  className="flex items-center gap-1.5 bg-red-500 text-white font-mono text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-40">
                  <Trash2 size={13} /> Delete{selectedIds.size ? ` (${selectedIds.size})` : ""}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="outro-editor">
            {/* ---- LIVE PREVIEW ---- */}
            <div className="space-y-2">
              <FieldLabel>Preview (placement & colours)</FieldLabel>
              <div className="mx-auto border-4 border-black rounded-2xl overflow-hidden shadow-[4px_4px_0px_#000] relative"
                style={{ ...bg, aspectRatio: `${asp.r}`, maxWidth: spec.aspect === "16:9" ? "100%" : "260px", width: "100%" }}
                data-testid="outro-preview">
                {spec.layers.map((l, i) => {
                  const [zx, zy] = ZONE_POS[l.zone] || [50, 50];
                  if (l.type === "image") {
                    const motionClass = l.motion && l.motion !== "none" ? `mist-motion-${l.motion}` : "";
                    return <div key={i} style={{ position: "absolute", left: `${zx}%`, top: `${zy}%`, transform: "translate(-50%,-50%)", width: `${(l.scale || 0.4) * 100}%` }}>
                      {logoUrl ? (
                        <img src={logoUrl} alt="logo" className={motionClass} data-testid={`outro-preview-logo-${i}`}
                          style={{ width: "100%", display: "block", transformOrigin: "center", ...motionDur(l) }} />
                      ) : (
                        <div className={`aspect-video grid place-items-center text-white/40 border-2 border-dashed border-white/25 rounded ${motionClass}`}><ImageIcon size={22} /></div>
                      )}
                    </div>;
                  }
                  const tMotion = l.motion && l.motion !== "none" ? `mist-motion-${l.motion}` : "";
                  return <div key={i} style={{ position: "absolute", left: `${zx}%`, top: `${zy}%`, transform: "translate(-50%,-50%)", width: "90%" }}>
                    <div className={tMotion} style={{
                      color: l.color, fontFamily: (FONTS.find((f) => f.k === l.font) || {}).css,
                      fontSize: `${Math.max(9, l.size * 0.16)}px`, fontWeight: 800, textAlign: l.align,
                      letterSpacing: `${l.tracking || 0}px`, whiteSpace: "pre-wrap", lineHeight: 1.15,
                      transformOrigin: "center", ...motionDur(l),
                    }}>{l.text}</div>
                  </div>;
                })}
                {isolating && (
                  <>
                    <div className="absolute inset-0 mist-checker" data-testid="outro-scan-checker" />
                    <div className="mist-scan-beam" data-testid="outro-scan-beam" />
                    <div className="absolute inset-0 flex items-end justify-center pb-2 pointer-events-none">
                      <span className="flex items-center gap-1 bg-black/70 text-[#39FF14] font-mono text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-full border border-[#39FF14]/50">
                        <ScanLine size={10} /> Isolating subject…
                      </span>
                    </div>
                  </>
                )}
              </div>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="outro-name"
                placeholder="Outro name" className="w-full border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-xs outline-none" />
              <button onClick={render} disabled={busy} data-testid="outro-render"
                className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />} {busy ? "Rendering…" : "Render & Save"}
              </button>
              {busy && (
                <div className="flex justify-center pt-1"><RenderProgress label="Rendering your outro" testid="outro-progress" /></div>
              )}
            </div>

            {/* ---- CONTROLS ---- */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Aspect</FieldLabel>
                  <select value={spec.aspect} onChange={(e) => patch({ aspect: e.target.value })} data-testid="outro-aspect"
                    className="w-full border-2 border-black bg-[#141414] text-white px-2 py-2 font-mono text-[11px] outline-none">
                    {ASPECTS.map((a) => <option key={a.k} value={a.k}>{a.k}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Duration · {spec.duration}s</FieldLabel>
                  <input type="range" min="2" max="20" step="1" value={spec.duration} data-testid="outro-duration"
                    onChange={(e) => patch({ duration: +e.target.value })} className="w-full accent-[#00F5D4] mt-2" />
                </div>
              </div>

              {/* Background */}
              <div>
                <FieldLabel>Background</FieldLabel>
                <div className="grid grid-cols-3 gap-1.5 mb-2" data-testid="outro-bg-modes">
                  {["color", "gradient", "image"].map((t) => (
                    <button key={t} onClick={() => patchBg({ type: t })} data-testid={`outro-bg-${t}`}
                      className={`border-2 border-black py-1.5 font-mono text-[9px] font-black uppercase shadow-[2px_2px_0px_#000] ${spec.background.type === t ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>{t}</button>
                  ))}
                </div>
                {spec.background.type !== "image" ? (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 font-mono text-[9px] text-white/60">Top
                      <input type="color" value={spec.background.color} onChange={(e) => patchBg({ color: e.target.value })} data-testid="outro-bg-color-picker" className="w-8 h-8 bg-transparent" /></label>
                    {spec.background.type === "gradient" && (
                      <label className="flex items-center gap-2 font-mono text-[9px] text-white/60">Bottom
                        <input type="color" value={spec.background.color2 || "#000000"} onChange={(e) => patchBg({ color2: e.target.value })} data-testid="outro-bg-color2" className="w-8 h-8 bg-transparent" /></label>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => bgRef.current?.click()} disabled={busy} data-testid="outro-bg-upload"
                      className="flex items-center gap-1.5 bg-charcoal text-gold font-black font-mono text-[9px] uppercase px-3 py-2 border-2 border-gold rounded-lg"><Upload size={12} /> Upload</button>
                    <VaultPickButton onFile={(f) => uploadBg({ target: { files: [f], value: "" } })} bucket="images" testid="outro-bg-vault" disabled={busy} label="Vault"
                      className="flex items-center gap-1.5 bg-black text-[#00F5D4] font-black font-mono text-[9px] uppercase px-3 py-2 border-2 border-[#00F5D4] rounded-lg" />
                    {bgAssetId && <button onClick={fixBg} disabled={busy} data-testid="outro-bg-fix"
                      className="flex items-center gap-1.5 bg-[#F9E27D] text-black font-black font-mono text-[9px] uppercase px-3 py-2 border-2 border-black rounded-lg"><Wand2 size={12} /> AI Fix</button>}
                    <input ref={bgRef} type="file" hidden accept="image/*" onChange={uploadBg} />
                  </div>
                )}
                {spec.background.type === "image" && scenes.length > 0 && (
                  <select value={bgAssetId} onChange={(e) => setBgAssetId(e.target.value)} data-testid="outro-bg-scene"
                    className="mt-2 w-full border-2 border-black bg-[#141414] text-white px-2 py-2 font-mono text-[10px] outline-none">
                    <option value="">…or pick a character scene</option>
                    {scenes.map((s) => <option key={s.id} value={s.id}>{s.title || s.filename}</option>)}
                  </select>
                )}
                {spec.background.type === "image" && (
                  <button type="button" onClick={toggleIsolate} disabled={isolating || !bgAssetId}
                    data-testid="outro-isolate-toggle"
                    className="mt-3 w-full flex items-start gap-2.5 text-left border-2 border-black rounded-lg p-2.5 bg-[#0d0d0d] disabled:opacity-50">
                    <span className={`shrink-0 mt-0.5 w-5 h-5 grid place-items-center border-2 border-black rounded ${isolate ? "bg-[#39FF14]" : "bg-[#141414]"}`}>
                      {isolating ? <Loader2 size={12} className="animate-spin text-black" />
                        : isolate ? <Check size={13} strokeWidth={3} className="text-black" /> : null}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[10px] font-black uppercase tracking-widest text-white">Isolate Subject &amp; Freeze Face</span>
                      <span className="block mt-0.5 font-mono text-[8px] uppercase tracking-wide text-white/45 leading-relaxed">Locks the character extracted from your baseline seed image for persistent identity scene sequencing.</span>
                    </span>
                  </button>
                )}
              </div>

              {/* Layers */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <FieldLabel>Layers</FieldLabel>
                  <div className="flex gap-1.5">
                    <button onClick={() => spec.layers.filter((l) => l.type === "text").length < 5 && addLayer(newTextLayer)} data-testid="outro-add-text"
                      className="flex items-center gap-1 bg-charcoal text-[#00F5D4] font-mono text-[8px] font-black uppercase px-2 py-1 rounded-full border border-black"><Type size={11} /> Text</button>
                    <button onClick={() => logoRef.current?.click()} data-testid="outro-add-logo"
                      className="flex items-center gap-1 bg-charcoal text-gold font-mono text-[8px] font-black uppercase px-2 py-1 rounded-full border border-black"><ImageIcon size={11} /> Logo</button>
                    <VaultPickButton onFile={(f) => uploadLogo({ target: { files: [f], value: "" } })} bucket="images" testid="outro-logo-vault" label="Vault"
                      className="flex items-center gap-1 bg-black text-[#00F5D4] font-mono text-[8px] font-black uppercase px-2 py-1 rounded-full border border-[#00F5D4]" />
                    <input ref={logoRef} type="file" hidden accept="image/*" onChange={uploadLogo} />
                  </div>
                </div>
                <div className="space-y-2">
                  {spec.layers.map((l, i) => (
                    <div key={i} className="border-2 border-[#2a2a2a] rounded-lg p-2.5 space-y-2 bg-[#0d0d0d]" data-testid={`outro-layer-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[8px] font-black uppercase tracking-widest text-white/50">{l.type === "image" ? "Logo" : "Text"} #{i + 1}</span>
                        <button onClick={() => removeLayer(i)} data-testid={`outro-layer-remove-${i}`} className="text-white/40"><Trash2 size={13} /></button>
                      </div>
                      {l.type === "text" && (
                        <>
                          <input value={l.text} onChange={(e) => patchLayer(i, { text: e.target.value })} data-testid={`outro-layer-text-${i}`}
                            className="w-full border-2 border-black bg-[#141414] text-white px-2 py-1.5 font-mono text-[11px] outline-none" />
                          <div className="grid grid-cols-2 gap-2">
                            <select value={l.font} onChange={(e) => patchLayer(i, { font: e.target.value })} data-testid={`outro-layer-font-${i}`} className="border-2 border-black bg-[#141414] text-white px-1 py-1 font-mono text-[10px]">
                              {FONTS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
                            </select>
                            <label className="flex items-center gap-1 font-mono text-[9px] text-white/50">Colour
                              <input type="color" value={l.color} onChange={(e) => patchLayer(i, { color: e.target.value })} data-testid={`outro-layer-color-${i}`} className="w-7 h-7 bg-transparent" /></label>
                          </div>
                          <label className="block font-mono text-[9px] text-white/50">Size · {l.size}
                            <input type="range" min="24" max="160" value={l.size} onChange={(e) => patchLayer(i, { size: +e.target.value })} className="w-full accent-[#00F5D4]" /></label>
                          <div>
                            <FieldLabel>Text motion (sync w/ logo)</FieldLabel>
                            <div className="grid grid-cols-4 gap-1.5" data-testid={`outro-textmotions-${i}`}>
                              {TEXT_MOTIONS.map((m) => (
                                <button key={m.k} onClick={() => patchLayer(i, { motion: m.k })} data-testid={`outro-textmotion-${i}-${m.k}`}
                                  className={`border-2 border-black py-1.5 font-mono text-[8px] font-black uppercase shadow-[2px_2px_0px_#000] ${(l.motion || "none") === m.k ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>{m.label}</button>
                              ))}
                            </div>
                            {PERIODIC.includes(l.motion) && (
                              <label className="block mt-2 font-mono text-[9px] text-white/50">Speed · {speedLabel(l.swing_speed)}
                                <input type="range" min="0.25" max="1.2" step="0.05" value={l.swing_speed ?? 0.5} data-testid={`outro-text-speed-${i}`}
                                  onChange={(e) => patchLayer(i, { swing_speed: +e.target.value })} className="w-full accent-[#00F5D4]" /></label>
                            )}
                          </div>
                        </>
                      )}
                      {l.type === "image" && (
                        <>
                          <label className="block font-mono text-[9px] text-white/50">Scale · {Math.round((l.scale || 0.4) * 100)}%
                            <input type="range" min="10" max="90" value={Math.round((l.scale || 0.4) * 100)} onChange={(e) => patchLayer(i, { scale: +e.target.value / 100 })} className="w-full accent-[#00F5D4]" /></label>
                          <div>
                            <FieldLabel>Animate motion</FieldLabel>
                            <div className="grid grid-cols-3 gap-1.5" data-testid={`outro-layer-motions-${i}`}>
                              {MOTIONS.map((m) => (
                                <button key={m.k} onClick={() => patchLayer(i, { motion: m.k })} data-testid={`outro-layer-motion-${i}-${m.k}`}
                                  className={`border-2 border-black py-1.5 font-mono text-[8px] font-black uppercase shadow-[2px_2px_0px_#000] ${(l.motion || "none") === m.k ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>{m.label}</button>
                              ))}
                            </div>
                            {PERIODIC.includes(l.motion) && (
                              <label className="block mt-2 font-mono text-[9px] text-white/50">Speed · {speedLabel(l.swing_speed)}
                                <input type="range" min="0.25" max="1.2" step="0.05" value={l.swing_speed ?? 0.5} data-testid={`outro-layer-speed-${i}`}
                                  onChange={(e) => patchLayer(i, { swing_speed: +e.target.value })} className="w-full accent-[#00F5D4]" /></label>
                            )}
                          </div>
                        </>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <select value={l.zone} onChange={(e) => patchLayer(i, { zone: e.target.value })} data-testid={`outro-layer-zone-${i}`} className="border-2 border-black bg-[#141414] text-white px-1 py-1 font-mono text-[10px]">
                          {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                        </select>
                        <select value={l.animation} onChange={(e) => patchLayer(i, { animation: e.target.value })} data-testid={`outro-layer-anim-${i}`} className="border-2 border-black bg-[#141414] text-white px-1 py-1 font-mono text-[10px]">
                          {ANIMS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-2 font-mono text-[9px] text-white/50">
                        <label>In · {l.start}s<input type="range" min="0" max={spec.duration} step="0.1" value={l.start} onChange={(e) => patchLayer(i, { start: +e.target.value })} className="w-full accent-[#00F5D4]" /></label>
                        <label>Out · {l.end}s<input type="range" min="0" max={spec.duration} step="0.1" value={l.end} onChange={(e) => patchLayer(i, { end: +e.target.value })} className="w-full accent-[#00F5D4]" /></label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Music */}
              <div>
                <FieldLabel>Music sting (optional · ElevenLabs)</FieldLabel>
                <input value={musicPrompt} onChange={(e) => setMusicPrompt(e.target.value)} data-testid="outro-music"
                  placeholder="e.g. upbeat synthwave, 110 BPM" className="w-full border-2 border-black bg-[#141414] text-white px-3 py-2 font-mono text-xs outline-none" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
